import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { t } from '../../lib/i18n';
import {
  LayoutDashboard, Users, MessageSquare, FileText,
  Mail, Phone, Settings, Brain, ChevronLeft, ChevronRight, Search,
  Package, FolderOpen, Kanban, Briefcase, Target, Bot, Building2, BarChart3,
  Shield, FileSignature, CalendarDays, Dna, Camera, Gavel, Radar, Bell, Rss, Clapperboard,
  Sparkles, Lightbulb, Handshake, Zap, ChevronDown, MoreHorizontal,
  Compass, TrendingUp, Database, BarChart, Archive, CreditCard, BookOpen,
  Wrench, ZapOff,
} from 'lucide-react';
import { Button } from '../ui/button';

// ============================================================
// IA reorganizacja 2026-05-14 (Etap 1)
// 5 sekcji tematycznych + Legacy + Settings/Admin
// Cel: zamknąć 41 stron w 14 widocznych pozycjach.
// ============================================================

// hideForRoles: lista ról dla których item ma być NIEWIDOCZNY w nav (i redirect z URL przez RoleGate)
// hideForPersona: lista person (seller_led | designer) dla których item ma być ukryty.
//   designer = projektant wnętrz/architekt — nie wystawia ofert do BZP, nie potrzebuje
//   B2B database dostawców (T10 2026-05-27, P1 follow-up z journal 2026-05-26).
const navGroups = [
  {
    key: 'opportunities',
    label: 'Źródła okazji',
    icon: Compass,
    items: [
      { key: 'tenders',     path: '/tenders',     icon: Gavel, pipeline: 'A',
        description: 'Formalne ogłoszenia BZP / TED',
        hideForPersona: ['designer'] /* designer nie startuje w przetargach jako oferent */ },
      { key: 'investments', path: '/investments', icon: TrendingUp, pipeline: 'B',
        description: 'Żywe inwestycje (GUNB) — wbij się przez projektanta',
        comingSoon: true },
      { key: 'preTender',   path: '/pre-tender',  icon: Radar,
        description: 'Sygnały przed ogłoszeniem (plany / projekty / news)' },
      { key: 'subscriptions', path: '/subscriptions', icon: Rss,
        description: 'Twoje obserwowane', hideForRoles: ['handlowiec'],
        hideForPersona: ['designer'] /* BZP newsletter — nieaplikowalne */ },
      { key: 'icp', path: '/icp', icon: Target,
        description: 'Silnik pozyskiwania — zdefiniuj kogo szukasz, AI przeszuka internet' },
    ],
  },
  {
    key: 'workflow',
    label: 'Proces ofertowy',
    icon: Handshake,
    items: [
      { key: 'inbox',         path: '/inbox',  icon: Mail,
        description: 'Skrzynka multi-mailbox z klasyfikacją AI' },
      { key: 'offerCreator',  path: '/offers', icon: FileText,
        description: 'Generator ofert + PDF', hideForRoles: ['handlowiec'] },
      { key: 'proposals',     path: '/proposals', icon: FileSignature,
        description: 'Oferty wysłane i podpisane', hideForRoles: ['handlowiec'] },
      { key: 'videoStudio',   path: '/video-studio', icon: Clapperboard,
        description: 'Brief wideo sprzedażowego z przetargu/inwestycji (AI)' },
    ],
  },
  {
    key: 'database',
    label: 'Baza danych',
    icon: Database,
    items: [
      { key: 'suppliers',    path: '/suppliers',    icon: Building2,
        description: 'Dostawcy + Moi opiekunowie',
        hideForPersona: ['designer'] /* designer ma swoich dostawców opraw — nie chce naszego B2B */ },
      { key: 'contractors',  path: '/contractors',  icon: Users,
        description: 'Klienci / kontrahenci', hideForRoles: ['handlowiec'] },
      { key: 'products',     path: '/products',     icon: Package,
        description: 'Katalog produktów (36k+)' /* dla obu person — designer wybiera oprawy, seller je sprzedaje */ },
      { key: 'architects',   path: '/architects',   icon: Briefcase,
        description: 'Architekci budowlani (z GUNB pozwoleń) — projekty budynków' },
      // T17 2026-05-27 — osobna kategoria projektantów wnętrz (NIE są w GUNB)
      { key: 'interiorDesigners', path: '/interior-designers', icon: Briefcase, pipeline: 'C',
        description: 'Projektanci wnętrz — faza wykończeniowa, kluczowi dla zleceń wyposażenia' },
    ],
  },
  {
    key: 'analytics',
    label: 'AI & Analityka',
    icon: BarChart,
    items: [
      { key: 'myDashboard',  path: '/my-dashboard', icon: LayoutDashboard,
        description: 'Mój dashboard (widgety drag-resize)' },
      { key: 'myReports',    path: '/zgloszenia',   icon: Bell,
        description: 'Moje zgłoszenia problemów (status + potwierdzenia)' },
      { key: 'feed',         path: '/feed',         icon: Bell,
        description: 'Codzienny briefing Adama' },
      { key: 'automations',  path: '/automations',  icon: Zap,
        description: 'Automation Engine (Gdy X → Zrób Y)', hideForRoles: ['handlowiec'] },
      { key: 'intelligence', path: '/intelligence', icon: Shield,
        description: 'Centrum wywiadowcze (SPHINX/Serper)', hideForRoles: ['handlowiec'],
        hideForPersona: ['designer'] /* SPHINX skupia się na zamawiających publicznych — designer nie potrzebuje */ },
      { key: 'reports',      path: '/reports',      icon: BarChart3,
        description: 'Raporty + statystyki', hideForRoles: ['handlowiec'] },
      { key: 'strategy',     path: '/strategy',     icon: Target,
        description: 'Planer strategii', hideForRoles: ['handlowiec'],
        hideForPersona: ['designer'] /* strategia bid/no-bid — nieaplikowalne */ },
    ],
  },
];

// ============================================================
// Sekcja "Narzędzia branżowe" — power-user features (Oświetlenie)
// Pokazywać tylko gdy industry === 'oswietlenie'
// ============================================================
const lightingTools = {
  key: 'lighting',
  label: 'Narzędzia branżowe',
  icon: Lightbulb,
  items: [
    { key: 'visualMatch',     path: '/visual-match',     icon: Camera,    hideForRoles: ['handlowiec'] },
    { key: 'projectAnalyzer', path: '/project-analyzer', icon: Building2, hideForRoles: ['handlowiec'] },
    { key: 'lightingAdvisor', path: '/lighting-advisor', icon: Sparkles },
    { key: 'catalogs',        path: '/catalogs',         icon: FolderOpen },
    { key: 'dialux',          path: '/dialux',           icon: Building2, hideForRoles: ['handlowiec'] },
  ],
};

// ============================================================
// Sekcja "Legacy" — stary Adam (CRM, negocjacje, calls)
// Zachowane dla istniejących użytkowników, schowane domyślnie.
// W przyszłości po decyzji biznesowej — usunąć lub re-promote.
// ============================================================
const legacyItems = [
  { key: 'dashboard',   path: '/dashboard',   icon: LayoutDashboard,
    note: 'Stary dashboard (Adam v1)' },
  { key: 'pipeline',    path: '/pipeline',    icon: Kanban,
    note: 'Stary pipeline negocjacji' },
  { key: 'negotiations', path: '/negotiations', icon: MessageSquare,
    note: 'Negocjacje (chat z agentem)' },
  { key: 'merchant',    path: '/merchant',    icon: Bot,
    note: 'Autonomiczny handlowiec (eksperymentalny)' },
  { key: 'leadQueue',   path: '/leads',       icon: BarChart3,
    note: 'Kolejka leadów (legacy)' },
  { key: 'meetings',    path: '/meetings',    icon: CalendarDays,
    note: 'Kalendarz spotkań' },
  { key: 'phoneCalls',  path: '/phone-calls', icon: Phone,
    note: 'Telefony / Twilio' },
  { key: 'research',    path: '/research',    icon: Search,
    note: 'Research kontrahentów (przeniesione do Klienci)' },
  { key: 'emailDrafts', path: '/emails',      icon: Mail,
    note: 'Stare drafty emaili (zastąpione przez Inbox Composer)' },
  { key: 'documents',   path: '/documents',   icon: FolderOpen,
    note: 'Archiwum dokumentów' },
  { key: 'genome',      path: '/genome',      icon: Dna,
    note: 'Genome dashboard (deep-tech)' },
  { key: 'agentConfig', path: '/agent-config', icon: Brain,
    note: 'Konfiguracja agenta (advanced)' },
  { key: 'agentRoles',  path: '/agent-roles', icon: Briefcase,
    note: 'Role agenta (advanced)' },
];

// Fallback labels dla nowych sekcji
const labelOverrides = {
  homeV3:           'Pulpit',
  tenders:          'Przetargi',
  investments:      'Inwestycje',
  preTender:        'Pre-Tender',
  subscriptions:    'Subskrypcje',
  icp:              'Pozyskiwanie (ICP)',
  inbox:            'Skrzynka',
  offerCreator:     'Oferty',
  proposals:        'Oferty wysłane',
  videoStudio:      'Video Studio',
  suppliers:        'Dostawcy',
  contractors:      'Klienci',
  products:         'Produkty',
  architects:       'Architekci',
  interiorDesigners: 'Projektanci wnętrz',
  // T15 fix 2026-05-27 — brakujące labels (były pokazywane jako klucze):
  myDashboard:      'Mój dashboard',
  myReports:        'Moje zgłoszenia',
  automations:      'Automatyzacje',
  feed:             'Adam Daily',
  intelligence:     'Wywiad',
  reports:          'Raporty',
  strategy:         'Strategia',
  visualMatch:      'Rozpoznaj zdjęcie',
  projectAnalyzer:  'Analizator projektów',
  lightingAdvisor:  'Doradca oświetlenia',
  catalogs:         'Katalogi PDF',
  dialux:           'DIALux',
  // Legacy
  dashboard:        'Dashboard (legacy)',
  pipeline:         'Pipeline (legacy)',
  negotiations:     'Negocjacje (legacy)',
  merchant:         'Handlowiec AI (legacy)',
  leadQueue:        'Leady (legacy)',
  meetings:         'Spotkania (legacy)',
  phoneCalls:       'Telefony (legacy)',
  research:         'Research (legacy)',
  emailDrafts:      'Drafty emaili (legacy)',
  documents:        'Dokumenty (legacy)',
  genome:           'Genome (advanced)',
  agentConfig:      'Konfiguracja agenta (advanced)',
  agentRoles:       'Role agenta (advanced)',
  admin:            'Admin',
  settings:         'Ustawienia',
  plans:            'Plany & ceny',
};

// Design refresh 2026-05-30 (T45) — kolory 3 pipeline'ów (spójne z HomeV3 PipelineStrip):
//   A=Przetargi(emerald), B=Inwestycje(amber), C=Projektanci wnętrz(fuchsia).
// Kropka przy pozycji nav = wizualny link do danego pipeline'u na Pulpicie.
const PIPELINE_DOT = {
  A: { rgb: '16,185,129',  title: 'Pipeline A — Przetargi' },
  B: { rgb: '245,158,11',  title: 'Pipeline B — Inwestycje' },
  C: { rgb: '217,70,239',  title: 'Pipeline C — Projektanci wnętrz' },
};

function PipelineDot({ pipeline, collapsed }) {
  const p = PIPELINE_DOT[pipeline];
  if (!p) return null;
  return (
    <span
      title={p.title}
      className={collapsed
        ? 'absolute top-1 right-1 w-1.5 h-1.5 rounded-full'
        : 'w-2 h-2 rounded-full flex-shrink-0'}
      style={{ background: `rgb(${p.rgb})`, boxShadow: `0 0 6px rgba(${p.rgb},0.7)` }}
    />
  );
}

function labelFor(key, lang) {
  // Override > i18n > key
  if (labelOverrides[key]) return labelOverrides[key];
  const fromI18n = t(key, lang);
  return fromI18n && fromI18n !== key ? fromI18n : key;
}


function _readUserRole() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u.role || 'viewer';
  } catch {
    return 'viewer';
  }
}

// T10 2026-05-27 (persona-aware sidebar): wczytaj persona z localStorage
// (App.js zapisuje user.preferences.persona). Default seller_led — backward
// compat dla userów bez persona ustawionego (nie powinno się zdarzyć po
// migracji 2026-05-27, ale bezpieczne).
function _readUserPersona() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u?.preferences?.persona || 'seller_led';
  } catch {
    return 'seller_led';
  }
}

function _visibleForRole(item, role) {
  return !(Array.isArray(item.hideForRoles) && item.hideForRoles.includes(role));
}

function _visibleForPersona(item, persona) {
  return !(Array.isArray(item.hideForPersona) && item.hideForPersona.includes(persona));
}

// Esencje — pokazywane w trybie "Simple" (default dla nowych userów).
// Zgłoszenie Izabeli 0475d485: "lista przeładowana, nie wiadomo co robić".
// Tryb Simple = tylko 3 grupy + Pulpit + Pomoc. Reszta po kliknięciu "Zaawansowane".
const ESSENTIAL_GROUP_KEYS = new Set(['opportunities']);  // tylko Źródła okazji w Simple
const ESSENTIAL_ITEM_KEYS  = new Set([
  // Z każdej grupy 1-2 najważniejsze elementy w trybie Simple:
  'tenders', 'investments', 'preTender', 'icp', 'inbox', 'suppliers',
  'architects', 'interiorDesigners',  // T57 — projektanci wnętrz zawsze widoczni (zniknęli dla seller_led/Simple)
  'myDashboard', 'myReports', 'feed',
]);

// T10 2026-05-27 — dla persona=designer dodajemy lighting tools do essentials,
// bo to ich core toolkit (a NIE seller_led toolkit, gdzie tendery są core).
// Suppliers/tenders są jednocześnie ukryte przez hideForPersona, więc nie ma konfliktu.
const DESIGNER_EXTRA_ESSENTIALS = new Set([
  'lightingAdvisor', 'dialux', 'projectAnalyzer', 'visualMatch', 'catalogs',
  'products', /* katalog opraw — kluczowy dla designera */
  'architects', /* sieć współpracowników */
  'interiorDesigners', /* T17 — sieć kolegów po fachu */
]);

function _readSimpleMode() {
  try {
    const v = localStorage.getItem('sidebar_simple_mode');
    // Default = "1" (Simple) dla nowych userów; ustawienie persistuje wybór.
    if (v === null) return true;
    return v === '1';
  } catch { return true; }
}

export function Sidebar({ lang, collapsed, onToggle }) {
  // T10 2026-05-27 — dla designer lighting tools domyślnie rozwinięte
  // (kluczowy toolkit), workflow zwinięty (mniej istotny — nie wystawia ofert).
  // Sterowane przez _readUserPersona() — odczyt z localStorage.
  const personaForDefaults = _readUserPersona();
  const isDesignerForDefaults = personaForDefaults === 'designer';
  const [openGroups, setOpenGroups] = useState({
    opportunities: true,
    workflow:      isDesignerForDefaults ? false : true,
    database:      true,
    analytics:     true,
    lighting:      isDesignerForDefaults,  // expanded dla designer, collapsed dla seller_led
    legacy:        false,
  });
  const [simpleMode, setSimpleMode] = useState(_readSimpleMode);

  const toggleGroup = (k) => setOpenGroups(g => ({ ...g, [k]: !g[k] }));
  const toggleSimpleMode = () => {
    const next = !simpleMode;
    setSimpleMode(next);
    try { localStorage.setItem('sidebar_simple_mode', next ? '1' : '0'); } catch {}
  };

  // Role-based filtering (2026-05-20) — handlowiec ma okrojony zakres
  const role = _readUserRole();
  const isHandlowiec = role === 'handlowiec';

  // T10 2026-05-27 — persona-based filtering. designer ukrywa items B2B-seller-only
  // (suppliers DB, BZP subscriptions, SPHINX intel, strategy bid/no-bid, tenders).
  // Lighting tools (DIALux, lightingAdvisor, projectAnalyzer) dla designer są ESSENTIAL.
  const persona = _readUserPersona();
  const isDesigner = persona === 'designer';

  // Filtruj itemy: (a) per rola, (b) per persona, (c) w trybie simple — tylko esencje
  const _filterItems = (items) => items.filter(it =>
    _visibleForRole(it, role)
    && _visibleForPersona(it, persona)
    && (!simpleMode || ESSENTIAL_ITEM_KEYS.has(it.key) || (isDesigner && DESIGNER_EXTRA_ESSENTIALS.has(it.key)))
  );
  const visibleNavGroups = navGroups
    .map(g => ({ ...g, items: _filterItems(g.items) }))
    .filter(g => g.items.length > 0);
  const visibleLightingItems = lightingTools.items.filter(it =>
    _visibleForRole(it, role)
    && _visibleForPersona(it, persona)
    && (!simpleMode || ESSENTIAL_ITEM_KEYS.has(it.key) || (isDesigner && DESIGNER_EXTRA_ESSENTIALS.has(it.key)))
  );

  return (
    <aside
      data-testid="sidebar-nav"
      className={`fixed left-0 top-0 h-screen bg-card border-r border-border flex flex-col z-40 transition-all duration-200 ${
        collapsed ? 'w-[60px]' : 'w-[240px]'
      }`}
    >
      {/* Logo */}
      <div className="flex items-center gap-2 px-3 h-14 border-b border-border">
        {!collapsed ? (
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                 style={{ background: 'linear-gradient(135deg, #2563eb, #0ea5e9 52%, #34d399)',
                          boxShadow: '0 0 16px rgba(37,99,235,0.45)' }}>
              <Zap className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-semibold text-sm tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
              MASKIL
            </span>
          </div>
        ) : (
          <div className="w-7 h-7 rounded-lg flex items-center justify-center mx-auto"
               style={{ background: 'linear-gradient(135deg, #2563eb, #0ea5e9 52%, #34d399)',
                        boxShadow: '0 0 16px rgba(37,99,235,0.45)' }}>
            <Zap className="w-4 h-4 text-primary-foreground" />
          </div>
        )}
      </div>

      <nav className="flex-1 py-2 px-2 space-y-2 overflow-y-auto">
        {/* Pulpit — pojedyncza pozycja na samej górze, bez sekcji */}
        <NavLink
          to="/home-v3"
          data-testid="sidebar-nav-homeV3"
          className={({ isActive }) =>
            `flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm transition-colors duration-150 ${
              isActive
                ? 'bg-primary/15 text-primary font-medium'
                : 'text-foreground/80 hover:bg-white/5 hover:text-foreground'
            } ${collapsed ? 'justify-center px-2' : ''}`
          }
        >
          <Sparkles className="w-4 h-4 flex-shrink-0" />
          {!collapsed && <span>{labelFor('homeV3', lang)}</span>}
        </NavLink>

        {/* Domain navigation — 4 główne sekcje (filtr po roli) */}
        {visibleNavGroups.map(group => {
          const GroupIcon = group.icon;
          const open = openGroups[group.key];
          return (
            <div key={group.key}>
              {!collapsed && (
                <button
                  onClick={() => toggleGroup(group.key)}
                  className="w-full flex items-center justify-between gap-2 px-2 py-1 mt-2 text-[10px] uppercase tracking-wider text-muted-foreground/70 hover:text-foreground transition-colors"
                  data-testid={`sidebar-group-${group.key}`}
                >
                  <span className="flex items-center gap-1.5">
                    <GroupIcon className="w-3 h-3" />
                    {group.label}
                  </span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${open ? '' : '-rotate-90'}`} />
                </button>
              )}
              {(open || collapsed) && (
                <div className={`mt-1 space-y-0.5 ${collapsed ? '' : 'pl-2 border-l border-border/40 ml-2'}`}>
                  {group.items.map(({ key, path, icon: Icon, description, comingSoon, pipeline }) => (
                    <NavLink
                      key={key}
                      to={path}
                      data-testid={`sidebar-nav-${key}`}
                      title={description || labelFor(key, lang)}
                      className={({ isActive }) =>
                        `relative flex items-center gap-3 px-2.5 py-1.5 rounded-lg text-sm transition-colors duration-150 ${
                          isActive
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-muted-foreground hover:bg-white/5 hover:text-foreground'
                        } ${collapsed ? 'justify-center px-2' : ''}`
                      }
                      end={path === '/'}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      {collapsed && <PipelineDot pipeline={pipeline} collapsed />}
                      {!collapsed && (
                        <span className="flex-1 flex items-center justify-between gap-1.5">
                          <span className="flex items-center gap-1.5">
                            {pipeline && <PipelineDot pipeline={pipeline} />}
                            {labelFor(key, lang)}
                          </span>
                          {comingSoon && (
                            <span className="text-[9px] uppercase tracking-wide px-1 rounded bg-primary/20 text-primary ml-1">
                              soon
                            </span>
                          )}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Narzędzia branżowe — domyślnie zwinięte; ukryte gdy puste po filtrze roli */}
        {!collapsed && visibleLightingItems.length > 0 && (
          <div>
            <button
              onClick={() => toggleGroup('lighting')}
              className="w-full flex items-center justify-between gap-2 px-2 py-1 mt-2 text-[10px] uppercase tracking-wider text-muted-foreground/70 hover:text-foreground transition-colors"
              data-testid="sidebar-group-lighting"
            >
              <span className="flex items-center gap-1.5">
                <Lightbulb className="w-3 h-3" />
                {lightingTools.label}
              </span>
              <ChevronDown className={`w-3 h-3 transition-transform ${openGroups.lighting ? '' : '-rotate-90'}`} />
            </button>
            {openGroups.lighting && (
              <div className="mt-1 space-y-0.5 pl-2 border-l border-border/40 ml-2">
                {visibleLightingItems.map(({ key, path, icon: Icon }) => (
                  <NavLink
                    key={key}
                    to={path}
                    data-testid={`sidebar-nav-${key}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-2.5 py-1.5 rounded-lg text-sm transition-colors duration-150 ${
                        isActive
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-muted-foreground hover:bg-white/5 hover:text-foreground'
                      }`
                    }
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span>{labelFor(key, lang)}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Legacy — całkowicie ukryte dla handlowca + w trybie Simple */}
        {!collapsed && !isHandlowiec && !simpleMode && (
          <div>
            <button
              onClick={() => toggleGroup('legacy')}
              className="w-full flex items-center justify-between gap-2 px-2 py-1 mt-2 text-[10px] uppercase tracking-wider text-muted-foreground/50 hover:text-foreground transition-colors"
              data-testid="sidebar-group-legacy"
            >
              <span className="flex items-center gap-1.5">
                <Archive className="w-3 h-3" />
                Legacy (Adam v1)
              </span>
              <ChevronDown className={`w-3 h-3 transition-transform ${openGroups.legacy ? '' : '-rotate-90'}`} />
            </button>
            {openGroups.legacy && (
              <div className="mt-1 space-y-0.5 pl-2 border-l border-border/30 ml-2">
                <div className="text-[10px] text-muted-foreground/60 italic px-2 py-1 leading-tight">
                  Strony z Adama v1 — zostają do czasu decyzji biznesowej. Nie używane w nowych flow.
                </div>
                {legacyItems.map(({ key, path, icon: Icon, note }) => (
                  <NavLink
                    key={key}
                    to={path}
                    data-testid={`sidebar-nav-${key}`}
                    title={note || labelFor(key, lang)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-2.5 py-1.5 rounded-lg text-xs transition-colors duration-150 ${
                        isActive
                          ? 'bg-zinc-700/30 text-zinc-200'
                          : 'text-muted-foreground/60 hover:bg-white/5 hover:text-muted-foreground'
                      }`
                    }
                  >
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{labelFor(key, lang)}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Bottom — Słownik + Plans + Admin + Settings + tryb Simple/Advanced */}
      <div className="px-2 pb-2 border-t border-border pt-2 space-y-0.5">
        <NavLink
          to="/glossary"
          data-testid="sidebar-nav-glossary"
          title="Słownik skrótów branżowych (SWZ, RFQ, BoQ, JEDZ…)"
          className={({ isActive }) =>
            `flex items-center gap-3 px-2.5 py-1.5 rounded-lg text-sm transition-colors duration-150 ${
              isActive
                ? 'bg-primary/10 text-primary font-medium'
                : 'text-muted-foreground hover:bg-white/5 hover:text-foreground'
            } ${collapsed ? 'justify-center px-2' : ''}`
          }
        >
          <BookOpen className="w-4 h-4 flex-shrink-0" />
          {!collapsed && <span>Słownik</span>}
        </NavLink>
        {!isHandlowiec && (
          <NavLink
            to="/plans"
            data-testid="sidebar-nav-plans"
            className={({ isActive }) =>
              `flex items-center gap-3 px-2.5 py-1.5 rounded-lg text-sm transition-colors duration-150 ${
                isActive
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-muted-foreground hover:bg-white/5 hover:text-foreground'
              } ${collapsed ? 'justify-center px-2' : ''}`
            }
          >
            <CreditCard className="w-4 h-4 flex-shrink-0" />
            {!collapsed && <span>{labelFor('plans', lang)}</span>}
          </NavLink>
        )}
        {!isHandlowiec && (
          <NavLink
            to="/admin"
            data-testid="sidebar-nav-admin"
            className={({ isActive }) =>
              `flex items-center gap-3 px-2.5 py-1.5 rounded-lg text-sm transition-colors duration-150 ${
                isActive
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-muted-foreground hover:bg-white/5 hover:text-foreground'
              } ${collapsed ? 'justify-center px-2' : ''}`
            }
          >
            <Shield className="w-4 h-4 flex-shrink-0" />
            {!collapsed && <span>{labelFor('admin', lang)}</span>}
          </NavLink>
        )}
        <NavLink
          to="/settings"
          data-testid="sidebar-nav-settings"
          className={({ isActive }) =>
            `flex items-center gap-3 px-2.5 py-1.5 rounded-lg text-sm transition-colors duration-150 ${
              isActive
                ? 'bg-primary/10 text-primary font-medium'
                : 'text-muted-foreground hover:bg-white/5 hover:text-foreground'
            } ${collapsed ? 'justify-center px-2' : ''}`
          }
        >
          <Settings className="w-4 h-4 flex-shrink-0" />
          {!collapsed && <span>{labelFor('settings', lang)}</span>}
        </NavLink>
        {/* Toggle Simple/Advanced — krytyczny dla zgł. Izabela 0475d485 */}
        {!collapsed && (
          <button
            type="button"
            onClick={toggleSimpleMode}
            className="w-full flex items-center justify-between gap-2 px-2 py-1 mt-1 text-[10px] uppercase tracking-wider text-muted-foreground/60 hover:text-foreground transition-colors"
            data-testid="sidebar-toggle-advanced"
            title={simpleMode
              ? 'Pokaż wszystkie moduły (Klienci, Negocjacje, Automation, Raporty, narzędzia branżowe…)'
              : 'Pokaż tylko podstawy (Inwestycje, Przetargi, Pre-Tender, Skrzynka…)'}
          >
            <span className="flex items-center gap-1.5">
              {simpleMode ? <Wrench className="w-3 h-3" /> : <ZapOff className="w-3 h-3" />}
              {simpleMode ? 'Pokaż zaawansowane' : 'Pokaż tylko podstawy'}
            </span>
          </button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          className="w-full justify-center mt-1"
          data-testid="sidebar-toggle"
          aria-label="Toggle sidebar"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>
      </div>
    </aside>
  );
}
